import { AFDataObject, AFMessageType } from '.';

export interface AdHistory {
  date: Date;
  type: string;
  impressions: number;
}

export interface TotalBoostsHistory {
  timestamp: Date;
  totalAmount: number;
  amount: number;
}

export interface CtoHistory {
  timestamp: Date;
  claimDate: Date;
}

export interface Link {
  type: string;
  label: string;
  url: string;
}

export interface DexScreenerRawData {
  tokenAddress: string;
  adHistories: AdHistory[];
  totalBoostsHistory: TotalBoostsHistory[];
  ctoHistories: CtoHistory[];
  links: Link[];
  lastUpdatedAt: number;
}

export interface DexScreenerStateData extends DexScreenerRawData {
  lastUpdated: Date;
}

export class DexScreener implements AFDataObject<DexScreenerRawData, DexScreenerStateData> {
  public tokenAddress: string;
  public adHistories: AdHistory[];
  public totalBoostsHistory: TotalBoostsHistory[];
  public ctoHistories: CtoHistory[];
  public links: Link[];
  public lastUpdatedAt: number;
  public lastUpdated: Date;

  constructor(data?: DexScreenerRawData | DexScreenerStateData | null, lastUpdated?: Date) {
    // 기본값 설정
    this.tokenAddress = data?.tokenAddress || '';
    this.adHistories = this.parseAdHistories(data?.adHistories || []);
    this.totalBoostsHistory = this.parseTotalBoostsHistory(data?.totalBoostsHistory || []);
    this.ctoHistories = this.parseCtoHistories(data?.ctoHistories || []);
    this.links = data?.links || [];
    this.lastUpdatedAt = data?.lastUpdatedAt || 0;
    this.lastUpdated = new Date(lastUpdated || 0);
  }

  static create(data?: DexScreenerRawData | DexScreenerStateData | null, lastUpdated?: Date): DexScreener {
    return new DexScreener(data, lastUpdated);
  }

  getMessageType(): AFMessageType {
    return AFMessageType.DEX_SCREENER;
  }

  /**
   * AdHistory 배열을 파싱합니다.
   * date를 Date 객체로 변환합니다.
   */
  private parseAdHistories(histories: any[]): AdHistory[] {
    return histories.map(history => ({
      date: history.date instanceof Date ? history.date : new Date(history.date),
      type: history.type || '',
      impressions: history.impressions || 0,
    }));
  }

  /**
   * TotalBoostsHistory 배열을 파싱합니다.
   * timestamp를 Date 객체로 변환합니다.
   */
  private parseTotalBoostsHistory(histories: any[]): TotalBoostsHistory[] {
    return histories.map(history => ({
      timestamp: history.timestamp instanceof Date ? history.timestamp : new Date(history.timestamp),
      totalAmount: history.totalAmount || 0,
      amount: history.amount || 0,
    }));
  }

  /**
   * CtoHistory 배열을 파싱합니다.
   * timestamp와 claimDate를 Date 객체로 변환합니다.
   */
  private parseCtoHistories(histories: any[]): CtoHistory[] {
    return histories.map(history => ({
      timestamp: history.timestamp instanceof Date ? history.timestamp : new Date(history.timestamp),
      claimDate: history.claimDate instanceof Date ? history.claimDate : new Date(history.claimDate),
    }));
  }

  toRawData(): DexScreenerRawData {
    return {
      tokenAddress: this.tokenAddress,
      adHistories: this.adHistories.map(history => ({
        date: new Date(history.date),
        type: history.type,
        impressions: history.impressions,
      })),
      totalBoostsHistory: this.totalBoostsHistory.map(history => ({
        timestamp: new Date(history.timestamp),
        totalAmount: history.totalAmount,
        amount: history.amount,
      })),
      ctoHistories: this.ctoHistories.map(history => ({
        timestamp: new Date(history.timestamp),
        claimDate: new Date(history.claimDate),
      })),
      links: this.links.map(link => ({ ...link })),
      lastUpdatedAt: this.lastUpdatedAt,
    };
  }

  toStateData(): DexScreenerStateData {
    return {
      ...this.toRawData(),
      lastUpdated: this.lastUpdated,
    };
  }

  filter(existingData: DexScreenerStateData): boolean {
    return Number(this.lastUpdated) > Number(existingData.lastUpdated);
  }
}

